"use client";
import { ArrowRight } from "lucide-react";
import { Service } from "@/app/lib/types";
import { services } from "@/app/lib/data";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from 'react';
import Image from "next/image";
import bgImage from "../../../public/service-bg.jpg";


const ServiceCard = ({ service, index }: { service: Service; index: number }) => {
  const cardRef = useRef(null);
  return (
      <motion.div
      key={index}
        ref={cardRef}
        className="group bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-100 p-8 border border-white/20 hover:border-white/40 relative overflow-hidden"
        whileHover={{ scale: 1.06 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 to-sky-700/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
      
      {/* Animated border effect */}
      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-sky-400/30 transition-all duration-500 pointer-events-none" />
      
      <div className="h-16 w-16 bg-sky-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-sky-200 transition-colors duration-300">
        <motion.div
          whileHover={{ scale: 1.1 }}
          className="text-sky-600 group-hover:text-sky-700 transition-colors duration-300"
        >
          {service.icon}
        </motion.div>
      </div>
      
      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-sky-700 transition-colors duration-300">
        {service.title}
      </h3>
      
      <p className="text-gray-600 mb-6 group-hover:text-gray-800 transition-colors duration-300">
        {service.description}
      </p>
      
      {/* <motion.a
        href="#"
        className="inline-flex items-center font-medium text-sky-600 hover:text-sky-700 transition-colors duration-300"
        whileHover={{ x: 5 }}
      >
        Learn more
        <motion.span
          initial={{ x: 0 }}
          whileHover={{ x: 5 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="ml-2"
        >
          <ArrowRight className="h-4 w-4" />
        </motion.span>
      </motion.a> */}
    </motion.div>
  );
};

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, margin: "0px 0px -100px 0px" });

  return (
    <section 
      ref={sectionRef}
      id="services" 
      className="py-24 bg-gradient-to-br from-sky-950 via-sky-600 to-sky-950 relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">

        {/* Background Image with overlay */}
        <div className="absolute inset-0 z-0">
          <Image
            src={bgImage}
            alt="Background"
            fill
            className="object-cover"
            quality={100}
            priority
          />
        </div>

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white"
            // style={{
            //   width: `${Math.random() * 10 + 5}px`,
            //   height: `${Math.random() * 10 + 5}px`,
            //   top: `${Math.random() * 100}%`,
            //   left: `${Math.random() * 100}%`,
            // }}
            // animate={{
            //   y: [0, (Math.random() - 0.5) * 40],
            //   x: [0, (Math.random() - 0.5) * 40],
            //   opacity: [0.1, 0.3, 0.1],
            // }}
            // transition={{
            //   duration: Math.random() * 10 + 10,
            //   repeat: Infinity,
            //   repeatType: "reverse",
            //   ease: "linear",
            // }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-100 to-sky-500">Services</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Discover how our AI-powered solutions can transform your business operations and drive success.
          </p>
        </motion.div>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
          initial="hidden"
          animate={isInView ? "visible" : {}}
        >
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
